// frontend/src/components/TrialCountdown.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TrialCountdown = ({
  trialEndDate,
  onExpired,
  userType = "business",
  hasPaymentMethod = false,
}) => {
  const [daysLeft, setDaysLeft] = useState(0);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [minutesLeft, setMinutesLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!trialEndDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const endDate = new Date(trialEndDate);
      const diff = endDate - now;

      if (diff <= 0) {
        setIsExpired(true);
        setDaysLeft(0);
        setHoursLeft(0);
        setMinutesLeft(0);
        if (onExpired) onExpired();
        return;
      }

      setIsExpired(false);

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setDaysLeft(days);
      setHoursLeft(hours);
      setMinutesLeft(minutes);
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [trialEndDate, onExpired]);

  if (!trialEndDate || daysLeft < 0) return null;

  // Trial expired
  if (isExpired) {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
          color: "white",
          padding: "20px 24px",
          borderRadius: "12px",
          marginBottom: "24px",
          boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
          border: "2px solid #dc2626",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "28px" }}>⏰</span>
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  marginBottom: "6px",
                }}
              >
                Trial Period Expired
              </div>
              <div style={{ fontSize: "14px", opacity: 0.95 }}>
                Your trial has ended. Subscribe now to continue using all
                features.
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              // Route to checkout preview page for trial expired
              const route =
                userType === "freelancer"
                  ? "/dashboard/freelancer/subscription/checkout"
                  : "/dashboard/settings/subscription/checkout";
              navigate(route);
            }}
            style={{
              background: "white",
              color: "#dc2626",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              transition: "transform 0.2s, box-shadow 0.2s",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
            }}
          >
            🚀 Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  // Trial active - show countdown
  // If payment method is added, downgrade urgency (they're all set to auto-subscribe)
  let urgency;
  if (hasPaymentMethod) {
    urgency = "secure"; // Payment method added - no urgency, they'll auto-subscribe
  } else {
    urgency = daysLeft <= 3 ? "critical" : daysLeft <= 7 ? "warning" : "normal";
  }

  const colors = {
    secure: {
      bg: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      border: "#10b981",
      shadow: "rgba(16, 185, 129, 0.3)",
    },
    critical: {
      bg: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
      border: "#dc2626",
      shadow: "rgba(220, 38, 38, 0.3)",
    },
    warning: {
      bg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      border: "#f59e0b",
      shadow: "rgba(245, 158, 11, 0.3)",
    },
    normal: {
      bg: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      border: "#3b82f6",
      shadow: "rgba(59, 130, 246, 0.3)",
    },
  };

  const color = colors[urgency];

  return (
    <div
      style={{
        background: color.bg,
        color: "white",
        padding: "20px 24px",
        borderRadius: "12px",
        marginBottom: "24px",
        boxShadow: `0 4px 12px ${color.shadow}`,
        border: `2px solid ${color.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "32px" }}>🎁</span>
          <div>
            <div
              style={{ fontSize: "14px", opacity: 0.95, marginBottom: "4px" }}
            >
              Trial Period
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "700",
                letterSpacing: "-0.5px",
              }}
            >
              {daysLeft > 0 ? (
                <>
                  {daysLeft} {daysLeft === 1 ? "day" : "days"}{" "}
                  {hoursLeft > 0 && `${hoursLeft}h`} left
                </>
              ) : hoursLeft > 0 ? (
                <>
                  {hoursLeft} {hoursLeft === 1 ? "hour" : "hours"} left
                </>
              ) : (
                <>
                  {Math.max(minutesLeft, 1)}{" "}
                  {Math.max(minutesLeft, 1) === 1 ? "minute" : "minutes"} left
                </>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            padding: "8px 16px",
            borderRadius: "8px",
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "2px" }}>
            Expires
          </div>
          <div style={{ fontSize: "14px", fontWeight: "600" }}>
            {new Date(trialEndDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
      </div>

      {urgency === "critical" && !hasPaymentMethod && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            background: "rgba(255, 255, 255, 0.15)",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ fontSize: "13px" }}>
            ⚡ Add payment method now to avoid service interruption
          </div>
          <button
            onClick={() => {
              // Route to payment method page for trial active
              const route =
                userType === "freelancer"
                  ? "/dashboard/freelancer/subscription/payment"
                  : "/dashboard/settings/subscription/payment";
              navigate(route);
            }}
            style={{
              background: "white",
              color: color.border,
              border: "none",
              padding: "8px 16px",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            💳 Add Payment Method
          </button>
        </div>
      )}

      {urgency === "secure" && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            background: "rgba(255, 255, 255, 0.15)",
            borderRadius: "6px",
            fontSize: "14px",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          ✅ Payment method saved - you'll automatically subscribe when trial
          ends. No action needed!
        </div>
      )}

      {urgency === "warning" && !hasPaymentMethod && (
        <div
          style={{
            marginTop: "12px",
            fontSize: "13px",
            opacity: 0.95,
          }}
        >
          💡 Consider adding payment method soon to avoid interruption
        </div>
      )}
    </div>
  );
};

export default TrialCountdown;
